import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { ChevronDown, Calendar, Check } from "lucide-react";

/*
  Claim-level boxes (CMS-1500 Boxes 10 / 14–16 / 18 / 20 / 22–23), redesigned.
  UI only. Writes the exact same fields and the same save call as before:
    payer.claim_frequency · prior_auth_number · claim_details.{related_to, accident_state,
    prior_illness_date, diagnosis_date, unable_from, unable_to, hosp_from, hosp_to,
    outside_lab, outside_lab_charges, resubmission_code, original_ref_no}
  Dates: shown/typed as MM/DD/YYYY, stored & sent as YYYY-MM-DD (unchanged payload).
*/

const INK = "#141412";
const FREQ_OPTS = [["1", "1 · Original"], ["7", "7 · Replacement"], ["8", "8 · Void"]];
const RELATED_OPTS = [["", "None"], ["employment", "Employment"], ["auto_accident", "Auto accident"], ["other_accident", "Other accident"]];
const LAB_OPTS = [["", "—"], ["no", "No"], ["yes", "Yes"]];
const DATE_FIELDS = ["prior_illness_date", "diagnosis_date", "unable_from", "unable_to", "hosp_from", "hosp_to"];
// Order used to pick the "first failing field" to focus on a blocked save (top→bottom in layout).
const ERR_ORDER = ["original_ref_no", "accident_state", "prior_illness_date", "diagnosis_date", "unable_from", "unable_to", "hosp_from", "hosp_to"];
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const pad = (n) => String(n).padStart(2, "0");
export const isoToDisplay = (iso) => { const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[2]}/${m[3]}/${m[1]}` : ""; };
// Parse a raw MM/DD/YYYY buffer → { empty, valid, iso }
export function parseBuf(buf) {
  const s = String(buf || "").trim();
  if (!s) return { empty: true, valid: false, iso: "" };
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return { empty: false, valid: false, iso: "" };
  const mo = +m[1], day = +m[2], yr = +m[3];
  if (mo < 1 || mo > 12 || day < 1 || day > 31 || yr < 1900 || yr > 2100) return { empty: false, valid: false, iso: "" };
  const d = new Date(yr, mo - 1, day);
  if (d.getFullYear() !== yr || d.getMonth() !== mo - 1 || d.getDate() !== day) return { empty: false, valid: false, iso: "" };
  return { empty: false, valid: true, iso: `${yr}-${pad(mo)}-${pad(day)}` };
}
// Auto-insert slashes as the user types (digits only, max 8 → MM/DD/YYYY)
export function formatTyping(raw) {
  const d = String(raw || "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}
const isoFromParts = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

/* ─────────────── revQ select (label + chevron, popover list) ─────────────── */
export function RevqSelect({ value, options, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [flip, setFlip] = useState(false);
  const [active, setActive] = useState(0);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const label = (options.find(([v]) => v === value) || options[0])[1];
  useEffect(() => {
    if (!open) return undefined;
    const r = btnRef.current?.getBoundingClientRect();
    setFlip(r ? window.innerHeight - r.bottom < 220 : false);
    setActive(Math.max(0, options.findIndex(([v]) => v === value)));
    const onDoc = (e) => { if (!popRef.current?.contains(e.target) && !btnRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (v) => { onChange(v); setOpen(false); btnRef.current?.focus(); };
  const onKey = (e) => {
    if (!open) { if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") { e.preventDefault(); setOpen(true); } return; }
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setOpen(false); btnRef.current?.focus(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(options.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(options[active][0]); }
  };
  return (
    <div className="clb-selwrap">
      <button ref={btnRef} type="button" data-testid={testid} className={`clb-ctl clb-sel${value && !(value === "1" || value === "") ? " clb-set" : ""}`}
        aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((o) => !o)} onKeyDown={onKey}>
        <span>{label}</span><ChevronDown size={14} className="clb-chev" />
      </button>
      {open ? (
        <div ref={popRef} className={`clb-pop${flip ? " clb-pop-up" : ""}`} role="listbox" data-testid={`${testid}-pop`}>
          {options.map(([v, l], i) => (
            <button type="button" key={v} role="option" aria-selected={v === value} data-testid={`${testid}-opt-${v || "none"}`}
              className={`clb-opt${i === active ? " clb-opt-active" : ""}`} onMouseEnter={() => setActive(i)} onClick={() => pick(v)}>
              <span>{l}</span>{v === value ? <Check size={14} /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ─────────────── revQ date picker popover ─────────────── */
function DatePicker({ valueIso, pairedIso, role, onPick, onClear, onClose, anchorRef }) {
  const base = (() => {
    const src = valueIso || (role === "to" ? pairedIso : "") || "";
    const m = String(src).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  })();
  const [view, setView] = useState(base);
  const [kbd, setKbd] = useState(null); // keyboard-focused iso, enables focus ring
  const [flip, setFlip] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const r = anchorRef.current?.getBoundingClientRect();
    setFlip(r ? window.innerHeight - r.bottom < 360 : false);
    const onDoc = (e) => { if (!ref.current?.contains(e.target) && !anchorRef.current?.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", onDoc);
    ref.current?.focus();
    return () => document.removeEventListener("mousedown", onDoc);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const todayIso = (() => { const d = new Date(); return isoFromParts(d.getFullYear(), d.getMonth(), d.getDate()); })();
  const first = new Date(view.y, view.m, 1);
  const startDow = first.getDay();
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(view.y, view.m, 1 - startDow + i);
    cells.push(d);
  }
  const lo = role === "to" ? pairedIso : null;       // "to" can't be before "from"
  const bandLo = valueIso && pairedIso ? (valueIso < pairedIso ? valueIso : pairedIso) : null;
  const bandHi = valueIso && pairedIso ? (valueIso < pairedIso ? pairedIso : valueIso) : null;

  const move = (days) => {
    const cur = kbd || valueIso || todayIso;
    const m = cur.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const d = new Date(+m[1], +m[2] - 1, +m[3]); d.setDate(d.getDate() + days);
    const niso = isoFromParts(d.getFullYear(), d.getMonth(), d.getDate());
    setKbd(niso); setView({ y: d.getFullYear(), m: d.getMonth() });
  };
  const onKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); anchorRef.current?.querySelector("input")?.focus(); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); move(-1); }
    else if (e.key === "ArrowRight") { e.preventDefault(); move(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); move(-7); }
    else if (e.key === "ArrowDown") { e.preventDefault(); move(7); }
    else if (e.key === "PageUp") { e.preventDefault(); setView((v) => ({ y: v.m === 0 ? v.y - 1 : v.y, m: (v.m + 11) % 12 })); }
    else if (e.key === "PageDown") { e.preventDefault(); setView((v) => ({ y: v.m === 11 ? v.y + 1 : v.y, m: (v.m + 1) % 12 })); }
    else if (e.key === "Enter") { e.preventDefault(); const t = kbd || valueIso; if (t && !(lo && t < lo)) onPick(t); }
  };
  return (
    <div ref={ref} tabIndex={-1} onKeyDown={onKey} className={`clb-cal${flip ? " clb-cal-up" : ""}`} data-testid="clb-datepicker">
      <div className="clb-cal-head">
        <button type="button" className="clb-cal-nav" aria-label="Previous year" onClick={() => setView((v) => ({ ...v, y: v.y - 1 }))}>«</button>
        <button type="button" className="clb-cal-nav" aria-label="Previous month" onClick={() => setView((v) => ({ y: v.m === 0 ? v.y - 1 : v.y, m: (v.m + 11) % 12 }))}>‹</button>
        <span className="clb-cal-title">{MONTHS[view.m]} {view.y}</span>
        <button type="button" className="clb-cal-nav" aria-label="Next month" onClick={() => setView((v) => ({ y: v.m === 11 ? v.y + 1 : v.y, m: (v.m + 1) % 12 }))}>›</button>
        <button type="button" className="clb-cal-nav" aria-label="Next year" onClick={() => setView((v) => ({ ...v, y: v.y + 1 }))}>»</button>
      </div>
      <div className="clb-cal-dow">{WEEKDAYS.map((d, i) => <span key={i}>{d}</span>)}</div>
      <div className="clb-cal-grid">
        {cells.map((d, i) => {
          const iso = isoFromParts(d.getFullYear(), d.getMonth(), d.getDate());
          const out = d.getMonth() !== view.m;
          const disabled = lo && iso < lo;
          const selected = iso === valueIso;
          const inBand = bandLo && bandHi && iso > bandLo && iso < bandHi;
          const cls = ["clb-day"];
          if (out) cls.push("clb-day-out");
          if (disabled) cls.push("clb-day-dis");
          if (selected) cls.push("clb-day-sel");
          if (inBand && !selected) cls.push("clb-day-band");
          if (iso === todayIso && !selected) cls.push("clb-day-today");
          if (iso === kbd) cls.push("clb-day-kbd");
          return (
            <button type="button" key={i} className={cls.join(" ")} disabled={disabled} tabIndex={-1}
              data-testid={`clb-day-${iso}`} onClick={() => { if (!disabled) onPick(iso); }}>{d.getDate()}</button>
          );
        })}
      </div>
      <div className="clb-cal-foot">
        <button type="button" className="clb-link" data-testid="clb-cal-today" onClick={() => { if (!(lo && todayIso < lo)) onPick(todayIso); }}>Today</button>
        {valueIso
          ? <button type="button" className="clb-link" data-testid="clb-cal-clear" onClick={onClear}>Clear</button>
          : <span className="clb-cal-hint">or type MM/DD/YYYY</span>}
      </div>
    </div>
  );
}

/* ─────────────── date field (text MM/DD/YYYY + calendar) ─────────────── */
export const DateField = forwardRef(function DateField({ buf, onBuf, iso, pairedIso, role, onPickIso, err, testid }, ref) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useImperativeHandle(ref, () => ({ focus: () => wrapRef.current?.querySelector("input")?.focus(), el: () => wrapRef.current }));
  const pick = (picked) => { onPickIso(picked); setOpen(false); };
  return (
    <div className="clb-datewrap" ref={wrapRef}>
      <input className={`clb-ctl clb-date${iso ? " clb-set" : ""}${err ? " clb-err-i" : ""}`} value={buf} data-testid={testid}
        placeholder="MM/DD/YYYY" inputMode="numeric" onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); if (open) { e.preventDefault(); setOpen(false); } } }}
        onChange={(e) => onBuf(formatTyping(e.target.value))} />
      <button type="button" className="clb-cal-btn" data-testid={`${testid}-cal`} aria-label="Open calendar" tabIndex={-1}
        onClick={() => setOpen((o) => !o)}><Calendar size={15} /></button>
      {open ? (
        <DatePicker valueIso={iso} pairedIso={pairedIso} role={role} anchorRef={wrapRef}
          onPick={pick} onClear={() => { onPickIso(""); setOpen(false); }} onClose={() => setOpen(false)} />
      ) : null}
    </div>
  );
});

/* ─────────────── small field primitives ─────────────── */
const TxtField = forwardRef(function TxtField({ value, onChange, placeholder, err, testid, prefix }, ref) {
  return (
    <div className={`clb-txtwrap${prefix ? " clb-txtwrap-pre" : ""}`} ref={ref}>
      {prefix ? <span className="clb-prefix">{prefix}</span> : null}
      <input className={`clb-ctl clb-txt${value ? " clb-set" : ""}${err ? " clb-err-i" : ""}`} value={value}
        placeholder={placeholder} data-testid={testid} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
});
const Lbl = ({ children, box }) => (
  <span className="clb-lbl">{children}{box ? <span className="clb-boxtag">{box}</span> : null}</span>
);
const Err = ({ msg, testid }) => (msg ? <span className="clb-errmsg" data-testid={testid}>{msg}</span> : null);
const Hint = ({ msg, testid }) => (msg ? <span className="clb-hint-amber" data-testid={testid}>{msg}</span> : null);

/* ─────────────── main ─────────────── */
const ClaimLevelBoxes = forwardRef(function ClaimLevelBoxes(
  { open, onToggle, resetKey, claimFrequency, onFreq, priorAuth, onPriorAuth, dos, cd, setCD }, ref) {

  // Raw MM/DD/YYYY buffers for the six date fields; seeded from the claim (cd) and on claim change.
  const [dateBuf, setDateBuf] = useState({});
  const [touched, setTouched] = useState({}); // blur-tracking for unparseable-date errors
  useEffect(() => {
    const seed = {}; DATE_FIELDS.forEach((f) => { seed[f] = isoToDisplay(cd[f]); });
    setDateBuf(seed); setTouched({});
  }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const fieldRefs = useRef({});
  const parsed = useMemo(() => { const o = {}; DATE_FIELDS.forEach((f) => { o[f] = parseBuf(dateBuf[f]); }); return o; }, [dateBuf]);

  const onDateBuf = (f, v) => {
    setDateBuf((b) => ({ ...b, [f]: v }));
    const p = parseBuf(v);
    if (p.empty) setCD(f, "");
    else if (p.valid) setCD(f, p.iso);
  };
  const onPickIso = (f, iso) => {
    setDateBuf((b) => {
      const next = { ...b, [f]: isoToDisplay(iso) };
      return next;
    });
    setCD(f, iso || "");
    setTouched((t) => ({ ...t, [f]: true }));
    // Picking a "from" opens its paired "to" when the "to" is empty.
    if (iso && (f === "unable_from" || f === "hosp_from")) {
      const toF = f === "unable_from" ? "unable_to" : "hosp_to";
      if (!cd[toF]) setTimeout(() => fieldRefs.current[toF]?.el()?.querySelector("input")?.focus(), 0);
    }
    // Changing "from" to after an existing "to" clears "to".
    if (f === "unable_from" && cd.unable_to && iso && iso > cd.unable_to) { setCD("unable_to", ""); setDateBuf((b) => ({ ...b, unable_to: "" })); }
    if (f === "hosp_from" && cd.hosp_to && iso && iso > cd.hosp_to) { setCD("hosp_to", ""); setDateBuf((b) => ({ ...b, hosp_to: "" })); }
  };

  // ── validation (inline; no toast) ──
  const errors = useMemo(() => {
    const e = {};
    DATE_FIELDS.forEach((f) => { const p = parsed[f]; if (!p.empty && !p.valid) e[f] = "Enter a date as MM/DD/YYYY"; });
    if (cd.related_to === "auto_accident" && !String(cd.accident_state || "").trim()) e.accident_state = "Required for an auto accident";
    if (!e.unable_to && parsed.unable_from.valid && parsed.unable_to.valid && parsed.unable_to.iso < parsed.unable_from.iso) e.unable_to = "“To” can’t be before “from”";
    if (!e.hosp_to && parsed.hosp_from.valid && parsed.hosp_to.valid && parsed.hosp_to.iso < parsed.hosp_from.iso) e.hosp_to = "“To” can’t be before “from”";
    if (String(cd.resubmission_code || "").trim() && !String(cd.original_ref_no || "").trim()) e.original_ref_no = "Required when there’s a resubmission code";
    return e;
  }, [parsed, cd]);
  // Unparseable-date errors only surface after blur or a save attempt.
  const shownErr = (f) => (DATE_FIELDS.includes(f) && errors[f] === "Enter a date as MM/DD/YYYY" && !touched[f]) ? undefined : errors[f];

  useImperativeHandle(ref, () => ({
    getErrors: () => ERR_ORDER.filter((f) => errors[f]).map((f) => ({ field: f, msg: errors[f] })),
    focusFirstError: () => {
      setTouched(Object.fromEntries(DATE_FIELDS.map((f) => [f, true])));
      const first = ERR_ORDER.find((f) => errors[f]);
      if (!first) return;
      const node = fieldRefs.current[first];
      const el = node?.el ? node.el() : node;
      el?.scrollIntoView?.({ block: "center", behavior: "smooth" });
      setTimeout(() => (node?.focus ? node.focus() : el?.querySelector?.("input")?.focus()), 60);
    },
  }), [errors]);

  // ── Box 22 non-blocking mismatch hint ──
  const resub = String(cd.resubmission_code || "").trim();
  const mismatch = resub && resub !== (claimFrequency || "1") ? `Doesn’t match claim frequency (${claimFrequency || "1"})` : null;

  // ── count + collapsed summary ──
  const freqSet = claimFrequency && claimFrequency !== "1";
  const summaryItems = useMemo(() => {
    const it = [];
    if (priorAuth) it.push(`Prior auth ${priorAuth}`);
    if (freqSet) it.push(`Frequency ${claimFrequency}`);
    if (resub) it.push(`Resubmission ${resub}`);
    if (cd.original_ref_no) it.push(`Ref ${cd.original_ref_no}`);
    if (cd.related_to) it.push(`Related to: ${(RELATED_OPTS.find(([v]) => v === cd.related_to) || [, ""])[1]}`);
    if (cd.accident_state) it.push(`Accident state ${cd.accident_state}`);
    if (cd.prior_illness_date) it.push(`Illness ${isoToDisplay(cd.prior_illness_date)}`);
    if (cd.diagnosis_date) it.push(`Similar illness ${isoToDisplay(cd.diagnosis_date)}`);
    if (cd.unable_from || cd.unable_to) it.push(`Unable to work ${isoToDisplay(cd.unable_from) || "—"}–${isoToDisplay(cd.unable_to) || "—"}`);
    if (cd.hosp_from || cd.hosp_to) it.push(`Hospitalized ${isoToDisplay(cd.hosp_from) || "—"}–${isoToDisplay(cd.hosp_to) || "—"}`);
    if (cd.outside_lab) it.push(`Outside lab: ${(LAB_OPTS.find(([v]) => v === cd.outside_lab) || [, ""])[1]}`);
    if (cd.outside_lab_charges !== "" && cd.outside_lab_charges != null) it.push(`Lab charges $${cd.outside_lab_charges}`);
    return it;
  }, [priorAuth, freqSet, claimFrequency, resub, cd]);
  const count = summaryItems.length;

  const setRef = (f) => (el) => { fieldRefs.current[f] = el; };

  return (
    <div className="clb-card" data-testid="cfv-claim-boxes">
      <div className={`clb-h${open ? " clb-h-open" : ""}`}>
        <b>Claim-level boxes</b>
        <span className="clb-count" data-testid="clb-count">{count ? `${count} set` : "all optional · none set"}</span>
        <button type="button" className="clb-link clb-toggle" data-testid="cfv-boxes-toggle" onClick={onToggle}>{open ? "Hide" : "Show"}</button>
        {!open && count ? <div className="clb-summary" data-testid="clb-summary" title={summaryItems.join(" · ")}>{summaryItems.join(" · ")}</div> : null}
      </div>
      {open ? (
        <div className="clb-grid" data-testid="clb-body">
          {/* SUBMISSION */}
          <section className="clb-sub">
            <div className="clb-sub-t">Submission</div>
            <div className="clb-fields">
              <label className="clb-field"><Lbl>Date of service</Lbl><div className="clb-ro" data-testid="cfv-box-dos-ro">{isoToDisplay(dos) || dos || "—"}</div></label>
              <label className="clb-field"><Lbl box="22">Claim frequency</Lbl>
                <RevqSelect value={claimFrequency || "1"} options={FREQ_OPTS} onChange={onFreq} testid="cfv-box-freq" /></label>
              <label className="clb-field"><Lbl box="23">Prior authorization</Lbl>
                <TxtField value={priorAuth || ""} onChange={(v) => onPriorAuth(v || null)} placeholder="—" testid="cfv-box-priorauth" /></label>
              <label className="clb-field"><Lbl box="22">Resubmission code</Lbl>
                <TxtField value={cd.resubmission_code || ""} onChange={(v) => setCD("resubmission_code", v)} placeholder="—" testid="cfv-box-resub-code" />
                <Hint msg={mismatch} testid="cfv-box-resub-mismatch" /></label>
              <label className="clb-field clb-span" ref={setRef("original_ref_no")}><Lbl box="22">Original ref. no.</Lbl>
                <TxtField value={cd.original_ref_no || ""} onChange={(v) => setCD("original_ref_no", v)} placeholder="Payer claim number" err={!!errors.original_ref_no} testid="cfv-box-original-ref" />
                <Err msg={errors.original_ref_no} testid="cfv-box-original-ref-err" /></label>
            </div>
          </section>

          {/* CONDITION */}
          <section className="clb-sub">
            <div className="clb-sub-t">Condition</div>
            <div className="clb-fields">
              <label className="clb-field"><Lbl box="10">Related to</Lbl>
                <RevqSelect value={cd.related_to || ""} options={RELATED_OPTS}
                  onChange={(v) => { setCD("related_to", v); if (v !== "auto_accident") setCD("accident_state", ""); }} testid="cfv-box-related-to" /></label>
              <label className="clb-field" ref={setRef("accident_state")}><Lbl box="10">Accident state</Lbl>
                <TxtField value={cd.accident_state || ""} onChange={(v) => setCD("accident_state", v.replace(/[^a-zA-Z]/g, "").slice(0, 2).toUpperCase())} placeholder="FL" err={!!errors.accident_state} testid="cfv-box-accident-state" />
                <Err msg={errors.accident_state} testid="cfv-box-accident-state-err" /></label>
              <label className="clb-field clb-span" ref={setRef("prior_illness_date")}><Lbl box="14">Current illness or injury</Lbl>
                <DateField ref={setRef("prior_illness_date")} buf={dateBuf.prior_illness_date || ""} onBuf={(v) => onDateBuf("prior_illness_date", v)} iso={cd.prior_illness_date || ""}
                  role="single" onPickIso={(iso) => onPickIso("prior_illness_date", iso)} err={!!shownErr("prior_illness_date")} testid="cfv-box-prior-illness-date" />
                <Err msg={shownErr("prior_illness_date")} testid="cfv-box-prior-illness-date-err" /></label>
            </div>
          </section>

          {/* DATES */}
          <section className="clb-sub">
            <div className="clb-sub-t">Dates</div>
            <div className="clb-fields">
              <label className="clb-field clb-span" ref={setRef("diagnosis_date")}><Lbl box="15">Similar illness</Lbl>
                <DateField ref={setRef("diagnosis_date")} buf={dateBuf.diagnosis_date || ""} onBuf={(v) => onDateBuf("diagnosis_date", v)} iso={cd.diagnosis_date || ""}
                  role="single" onPickIso={(iso) => onPickIso("diagnosis_date", iso)} err={!!shownErr("diagnosis_date")} testid="cfv-box-diagnosis-date" />
                <Err msg={shownErr("diagnosis_date")} testid="cfv-box-diagnosis-date-err" /></label>
              <label className="clb-field clb-span"><Lbl box="16">Unable to work</Lbl>
                <div className="clb-pair">
                  <div className="clb-pair-col" ref={setRef("unable_from")}>
                    <span className="clb-pair-l">From</span>
                    <DateField ref={setRef("unable_from")} buf={dateBuf.unable_from || ""} onBuf={(v) => onDateBuf("unable_from", v)} iso={cd.unable_from || ""}
                      pairedIso={cd.unable_to || ""} role="from" onPickIso={(iso) => onPickIso("unable_from", iso)} err={!!shownErr("unable_from")} testid="cfv-box-unable-from" />
                  </div>
                  <div className="clb-pair-col" ref={setRef("unable_to")}>
                    <span className="clb-pair-l">To</span>
                    <DateField ref={setRef("unable_to")} buf={dateBuf.unable_to || ""} onBuf={(v) => onDateBuf("unable_to", v)} iso={cd.unable_to || ""}
                      pairedIso={cd.unable_from || ""} role="to" onPickIso={(iso) => onPickIso("unable_to", iso)} err={!!(shownErr("unable_to"))} testid="cfv-box-unable-to" />
                  </div>
                </div>
                <Err msg={shownErr("unable_to")} testid="cfv-box-unable-to-err" /></label>
              <label className="clb-field clb-span"><Lbl box="18">Hospitalized</Lbl>
                <div className="clb-pair">
                  <div className="clb-pair-col" ref={setRef("hosp_from")}>
                    <span className="clb-pair-l">From</span>
                    <DateField ref={setRef("hosp_from")} buf={dateBuf.hosp_from || ""} onBuf={(v) => onDateBuf("hosp_from", v)} iso={cd.hosp_from || ""}
                      pairedIso={cd.hosp_to || ""} role="from" onPickIso={(iso) => onPickIso("hosp_from", iso)} err={!!shownErr("hosp_from")} testid="cfv-box-hosp-from" />
                  </div>
                  <div className="clb-pair-col" ref={setRef("hosp_to")}>
                    <span className="clb-pair-l">To</span>
                    <DateField ref={setRef("hosp_to")} buf={dateBuf.hosp_to || ""} onBuf={(v) => onDateBuf("hosp_to", v)} iso={cd.hosp_to || ""}
                      pairedIso={cd.hosp_from || ""} role="to" onPickIso={(iso) => onPickIso("hosp_to", iso)} err={!!(shownErr("hosp_to"))} testid="cfv-box-hosp-to" />
                  </div>
                </div>
                <Err msg={shownErr("hosp_to")} testid="cfv-box-hosp-to-err" /></label>
            </div>
          </section>

          {/* OUTSIDE LAB */}
          <section className="clb-sub">
            <div className="clb-sub-t">Outside lab</div>
            <div className="clb-fields">
              <label className="clb-field"><Lbl box="20">Outside lab</Lbl>
                <RevqSelect value={cd.outside_lab || ""} options={LAB_OPTS}
                  onChange={(v) => { setCD("outside_lab", v); if (v !== "yes") setCD("outside_lab_charges", ""); }} testid="cfv-box-outside-lab" /></label>
              <label className="clb-field"><Lbl box="20">Outside lab charges</Lbl>
                <TxtField value={cd.outside_lab_charges ?? ""} prefix="$" placeholder="0.00"
                  onChange={(v) => { const clean = v.replace(/[^0-9.]/g, ""); setCD("outside_lab_charges", clean === "" ? "" : Number(clean)); }} testid="cfv-box-outside-lab-charges" /></label>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
});

export default ClaimLevelBoxes;
